import React, { useState, useEffect, useMemo, useRef, useImperativeHandle, forwardRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@byscott-io/corebyscott";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import { api } from "../lib/api";

const ctl = "border border-gray-300 rounded px-2 py-1 text-sm";
const COLORS = ["#6366f1", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#06b6d4",
  "#ec4899", "#84cc16", "#f97316", "#14b8a6", "#a855f7", "#64748b", "#94a3b8"];
const num = (n) => (typeof n === "number" ? n.toLocaleString() : (n ?? "—"));
const cell = (v) => (v == null ? "—" : typeof v === "object" ? JSON.stringify(v) : String(v));

const EXAMPLES = [
  "SELECT count(*) FROM page_view SINCE 1 day ago",
  "SELECT count(*), avg(duration_ms) FROM api_request WHERE status >= 400 FACET endpoint SINCE 1 day ago ORDER BY count DESC LIMIT 20",
  "SELECT count(*) FROM api_request SINCE 7 days ago TIMESERIES 1 day",
];

// ---- Context-aware autocomplete -------------------------------------------
const CLAUSES = ["select", "from", "where", "facet", "since", "until", "timeseries", "order", "limit"];
const kw = (k) => ({ label: k, insert: k + " ", kind: "keyword" });
const FUNCS = [
  { label: "count(*)", insert: "count(*) ", kind: "func" },
  { label: "sum(…)", insert: "sum(", kind: "func" },
  { label: "avg(…)", insert: "avg(", kind: "func" },
  { label: "min(…)", insert: "min(", kind: "func" },
  { label: "max(…)", insert: "max(", kind: "func" },
  { label: "uniqueCount(…)", insert: "uniqueCount(", kind: "func" },
];
const OPS = ["=", "!=", ">", ">=", "<", "<="].map((o) => ({ label: o, insert: o + " ", kind: "op" }));
const WHERE_KW = ["AND", "OR", "LIKE", "NOT LIKE", "IN", "NOT IN", "IS NULL", "IS NOT NULL"].map(kw);
const TIMES = ["1 hour ago", "24 hours ago", "7 days ago", "30 days ago", "today", "yesterday", "now"]
  .map((t) => ({ label: t, insert: t + " ", kind: "time" }));
const INTERVALS = ["1 hour", "1 day", "1 week", "auto"].map((t) => ({ label: t, insert: t + " ", kind: "time" }));
const KIND_COLOR = { keyword: "text-purple-600", func: "text-emerald-600", source: "text-blue-600",
  field: "text-gray-500", op: "text-amber-600", time: "text-rose-500", value: "text-cyan-600", sample: "text-teal-600" };

function currentToken(text, cursor) {
  let start = cursor;
  while (start > 0 && /[\w.*]/.test(text[start - 1])) start--;
  return { token: text.slice(start, cursor), start };
}

// Which clause is the cursor in? Last top-level clause keyword before it.
function lastClause(before) {
  const words = (before.toLowerCase().match(/[a-z_]+/g) || []);
  let clause = null;
  words.forEach((w) => { if (CLAUSES.includes(w)) clause = w; });
  return clause;
}

const AGG_FUNCS = new Set(["count", "sum", "avg", "average", "min", "max", "uniquecount", "uniques", "count_distinct"]);

// The aggregate function whose argument the cursor sits in, else null. Walks
// back to the innermost unclosed "(" and reads the word before it.
function openAggFunction(before) {
  let depth = 0;
  for (let i = before.length - 1; i >= 0; i--) {
    const ch = before[i];
    if (ch === ")") depth++;
    else if (ch === "(") {
      if (depth === 0) {
        const m = before.slice(0, i).match(/([A-Za-z_]+)\s*$/);
        return m ? m[1].toLowerCase() : null;
      }
      depth--;
    }
  }
  return null;
}

// In WHERE, when the cursor is right after `field <op>`, return that field so we
// can offer its actual values. Only before a quote is typed (we insert quoted).
function valueField(before, clause) {
  if (clause !== "where") return null;
  const { start } = currentToken(before, before.length);
  const pre = before.slice(0, start).replace(/\s+$/, "");
  const m = pre.match(/([A-Za-z_][\w.]*)\s*(?:=|!=|<>|>=|<=|>|<)$/);
  return m ? m[1] : null;
}

function suggestFor(text, cursor, events, properties, samples, values) {
  const before = text.slice(0, cursor);
  const { token, start } = currentToken(text, cursor);
  const clause = lastClause(before);
  const vfield = valueField(before, clause);
  if (vfield) return { items: [], token, start, valueField: vfield };
  // sample.<metric> / value.<metric> sources aggregate a single `value` field.
  const fromSrc = (text.match(/\bfrom\s+((?:sample|value)\.[\w.]+|[A-Za-z_][\w.]*)/i) || [])[1];
  const isMetricSrc = fromSrc && /^(sample|value)\./i.test(fromSrc);
  const props = isMetricSrc ? [{ name: "value", kind: "numeric" }] : (properties || []);
  const fieldItem = (p, closing) => ({ label: p.name, insert: p.name + (closing ? ") " : " "), kind: "field" });
  const fields = props.map((p) => fieldItem(p, false));
  const allClosing = props.map((p) => fieldItem(p, true));
  // sum/avg/min/max only make sense over numeric properties.
  const numericClosing = props.filter((p) => ["numeric", "mixed"].includes(p.kind)).map((p) => fieldItem(p, true));

  let items;
  const openFn = openAggFunction(before);
  if (openFn && AGG_FUNCS.has(openFn)) {
    // e.g. `sum(` -> numeric columns; `count(`/`uniqueCount(` -> `*` or any column
    if (["count", "count_distinct", "uniquecount"].includes(openFn)) {
      items = [{ label: "*", insert: "*) ", kind: "field" }, ...allClosing];
    } else {
      items = numericClosing.length ? numericClosing : allClosing;
    }
  } else {
    switch (clause) {
      case null: case "select": items = [...FUNCS, ...fields, kw("FROM")]; break;
      case "from": {
        const tl = token.toLowerCase();
        if (tl.startsWith("sample.")) {
          items = (samples || []).map((s) => ({ label: `sample.${s}`, insert: `sample.${s} `, kind: "sample" }));
        } else if (tl.startsWith("value.")) {
          items = (values || []).map((v) => ({ label: `value.${v}`, insert: `value.${v} `, kind: "value" }));
        } else {
          items = [
            ...(events || []).map((e) => ({ label: e, insert: e + " ", kind: "source" })),
            { label: "events", insert: "events ", kind: "source" },
            ...((samples || []).length ? [{ label: "sample.", insert: "sample.", kind: "sample" }] : []),
            ...((values || []).length ? [{ label: "value.", insert: "value.", kind: "value" }] : []),
            kw("WHERE"), kw("FACET"), kw("SINCE"), kw("TIMESERIES"), kw("ORDER BY"), kw("LIMIT"),
          ];
        }
        break;
      }
      case "where": items = [...fields, ...OPS, ...WHERE_KW, kw("FACET"), kw("SINCE"), kw("TIMESERIES"), kw("ORDER BY"), kw("LIMIT")]; break;
      case "facet": items = [...fields, kw("SINCE"), kw("TIMESERIES"), kw("ORDER BY"), kw("LIMIT")]; break;
      case "since": case "until": items = [...TIMES, kw("UNTIL"), kw("TIMESERIES"), kw("ORDER BY"), kw("LIMIT")]; break;
      case "timeseries": items = [...INTERVALS, kw("ORDER BY"), kw("LIMIT")]; break;
      case "order": items = [{ label: "count", insert: "count ", kind: "field" }, ...fields, kw("DESC"), kw("ASC"), kw("LIMIT")]; break;
      default: items = [];
    }
  }

  const t = token.toLowerCase();
  const filtered = t ? items.filter((it) => it.label.toLowerCase().startsWith(t) || it.insert.toLowerCase().startsWith(t)) : items;
  return { items: filtered.slice(0, 40), token, start, valueField: null };
}

const QueryEditor = forwardRef(function QueryEditor({ value, onChange, onRun, events, properties, samples, values, appId, fromEvent }, fwdRef) {
  const ref = useRef(null);
  const [ctx, setCtx] = useState({ items: [], token: "", start: 0, valueField: null });
  const [active, setActive] = useState(0);
  const [dismissed, setDismissed] = useState(false);
  const [focused, setFocused] = useState(false);

  const recompute = () => {
    const el = ref.current;
    if (!el) return;
    setCtx(suggestFor(value, el.selectionStart, events, properties, samples, values));
    setActive(0);
    setDismissed(false);
  };
  useEffect(recompute, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  // Value autocomplete: after `field <op>` in WHERE, fetch that field's values.
  const { data: valuesData } = useQuery({
    queryKey: ["insight-values", appId, fromEvent, ctx.valueField],
    queryFn: () => api.getInsightValues(appId, fromEvent || undefined, ctx.valueField),
    enabled: !!appId && !!ctx.valueField,
  });

  const items = ctx.valueField
    ? (valuesData?.values || [])
        .filter((v) => String(v).toLowerCase().startsWith(ctx.token.toLowerCase()))
        .slice(0, 40)
        .map((v) => ({ label: String(v), insert: `'${String(v).replace(/'/g, "''")}' `, kind: "value" }))
    : ctx.items;
  const open = focused && !dismissed && items.length > 0;

  const accept = (item) => {
    if (!item) return;
    const el = ref.current;
    const cursor = el.selectionStart;
    const { start } = currentToken(value, cursor);
    const next = value.slice(0, start) + item.insert + value.slice(cursor);
    onChange(next);
    const pos = start + item.insert.length;
    requestAnimationFrame(() => { el.focus(); el.setSelectionRange(pos, pos); });
  };

  // Insert a token at the cursor (used by the schema sidebar), with spacing.
  const insertToken = (token) => {
    const el = ref.current;
    const cursor = el ? (el.selectionStart ?? value.length) : value.length;
    const head = value.slice(0, cursor);
    const lead = head.length && !/[\s(.]$/.test(head) ? " " : "";
    const ins = `${lead}${token} `;
    onChange(head + ins + value.slice(cursor));
    const pos = head.length + ins.length;
    requestAnimationFrame(() => { if (el) { el.focus(); el.setSelectionRange(pos, pos); } });
  };
  useImperativeHandle(fwdRef, () => ({ insertToken }));

  const onKeyDown = (e) => {
    if (open && items.length) {
      if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => (a + 1) % items.length); return; }
      if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => (a - 1 + items.length) % items.length); return; }
      if (e.key === "Tab") { e.preventDefault(); accept(items[Math.min(active, items.length - 1)]); return; }
      if (e.key === "Escape") { setDismissed(true); return; }
    }
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") { e.preventDefault(); onRun(); }
  };

  return (
    <div className="relative">
      <textarea ref={ref} value={value} onChange={(e) => onChange(e.target.value)}
        onKeyUp={recompute} onClick={recompute} onKeyDown={onKeyDown}
        onFocus={() => { setFocused(true); recompute(); }} onBlur={() => setTimeout(() => setFocused(false), 150)}
        spellCheck={false} rows={4}
        className="w-full font-mono text-sm border border-gray-300 rounded p-3 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
        placeholder="SELECT count(*) FROM page_view SINCE 1 day ago    ·    Tab to accept · ⌘/Ctrl+Enter to run" />
      {open && (
        <div className="absolute z-20 left-3 top-full mt-1 w-72 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {items.map((it, i) => (
            <button key={`${it.kind}-${it.label}`} onMouseDown={(e) => { e.preventDefault(); accept(it); }}
              className={`flex w-full items-center gap-2 px-3 py-1.5 text-left ${i === active ? "bg-indigo-50" : "hover:bg-gray-50"}`}>
              <span className={`text-[10px] uppercase w-14 shrink-0 ${KIND_COLOR[it.kind] || "text-gray-400"}`}>{it.kind}</span>
              <span className="font-mono text-sm text-gray-800">{it.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
});

const byBucketAsc = (a, b) => String(a.bucket).localeCompare(String(b.bucket));
const shortDate = (b) => { const s = String(b); return s.length > 10 ? s.slice(5, 16).replace("T", " ") : s.slice(5); };

// Pivot the envelope into recharts data + series keys for the chartable kinds.
// Time buckets are sorted ascending so the X-axis reads left-to-right.
function buildChart(kind, columns, rows) {
  const dims = columns.filter((c) => c.role === "dimension").map((c) => c.name);
  const measures = columns.filter((c) => c.role === "measure").map((c) => c.name);

  if (kind === "timeseries") {
    return { xKey: "bucket", keys: measures, data: [...rows].sort(byBucketAsc), timeX: true };
  }
  if (kind === "facet_timeseries") {
    const facetDims = dims.filter((d) => d !== "bucket");
    const measure = measures[0];
    const byBucket = {};
    const keys = new Set();
    rows.forEach((r) => {
      const label = facetDims.map((d) => r[d]).join(" / ") || "value";
      keys.add(label);
      byBucket[r.bucket] ||= { bucket: r.bucket };
      byBucket[r.bucket][label] = r[measure];
    });
    return { xKey: "bucket", keys: [...keys], data: Object.values(byBucket).sort(byBucketAsc), timeX: true };
  }
  if (kind === "facets") {
    return { xKey: dims[0], keys: measures, data: rows, timeX: false };
  }
  return { xKey: null, keys: [], data: [], timeX: false };
}

function RowsTable({ columns, rows, onRowClick }) {
  return (
    <div className="overflow-x-auto max-h-96">
      <table className="min-w-full divide-y divide-gray-200 text-sm">
        <thead className="bg-gray-50 sticky top-0">
          <tr>{columns.map((c) => (
            <th key={c.name} className={`px-4 py-2 text-left font-medium text-gray-500 ${c.role === "measure" ? "text-right" : ""}`}>{c.name}</th>
          ))}</tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((r, i) => (
            <tr key={i} onClick={onRowClick ? () => onRowClick(r) : undefined}
              className={`hover:bg-gray-50 ${onRowClick ? "cursor-pointer" : ""}`}>
              {columns.map((c) => (
                <td key={c.name} className={`px-4 py-2 ${c.role === "measure" ? "text-right text-gray-900" : "text-gray-600"}`}>
                  {c.role === "measure" ? num(r[c.name]) : cell(r[c.name])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AddColumn({ properties, shown, onAdd }) {
  const opts = ["occurred_at", "event", "user_id", "container_id", "visitor_id", "session_id",
    ...(properties || []).map((p) => p.name)]
    .filter((n, i, a) => a.indexOf(n) === i)
    .filter((n) => !shown.includes(n));
  if (!opts.length) return null;
  return (
    <select value="" onChange={(e) => e.target.value && onAdd(e.target.value)} className="text-xs border border-gray-300 rounded px-1 py-0.5 cursor-pointer">
      <option value="">+ column</option>
      {opts.map((n) => <option key={n} value={n}>{n}</option>)}
    </select>
  );
}

function ResultView({ result, appId, properties, chartType, setChartType }) {
  const { kind, columns = [], rows = [] } = result;
  const measures = columns.filter((c) => c.role === "measure").map((c) => c.name);
  const dims = columns.filter((c) => c.role === "dimension");
  const chart = useMemo(() => buildChart(kind, columns, rows), [kind, columns, rows]);
  const chartable = chart.data.length > 0 && chart.keys.length > 0 && chart.xKey;
  const drillable = kind !== "rows" && measures.length > 0;

  const [drill, setDrill] = useState(null);
  const runDrill = async (row, cols = null) => {
    const cell = {};
    dims.forEach((d) => { if (row[d.name] !== undefined) cell[d.name] = row[d.name]; });
    const label = dims.map((d) => `${d.name} = ${row[d.name]}`).join(" · ") || "all rows";
    setDrill({ loading: true, label, row, cols });
    try {
      setDrill({ label, row, cols, result: await api.drillQuery(appId, result.query, cell, cols) });
    } catch (e) {
      setDrill({ label, row, cols, error: e.message });
    }
  };
  const shownCols = drill?.result?.columns?.map((c) => c.name) || [];
  const setCols = (next) => runDrill(drill.row, next);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between text-xs text-gray-500">
        <span>{result.source} · {kind} · {rows.length} row{rows.length === 1 ? "" : "s"}
          {result.window?.from ? ` · ${result.window.from.slice(0, 16).replace("T", " ")} → ${result.window.to?.slice(0, 16).replace("T", " ")}` : ""}</span>
        {drillable && <button onClick={() => runDrill({})} className="text-indigo-600 hover:underline">⤢ rows behind all</button>}
      </div>

      {kind === "scalar" && (
        <div className="flex flex-wrap gap-4">
          {(measures.length ? measures : Object.keys(rows[0] || {})).map((m) => (
            <div key={m} className="bg-white rounded-lg shadow px-6 py-4">
              <div className="text-2xl font-bold text-gray-900">{num(rows[0]?.[m])}</div>
              <div className="text-xs text-gray-500 mt-1">{m}</div>
            </div>
          ))}
        </div>
      )}

      {chartable && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-end mb-3">
            <div className="flex rounded-lg border border-gray-200 overflow-hidden text-xs">
              {["line", "bar"].map((t) => (
                <button key={t} onClick={() => setChartType(t)}
                  className={`px-2 py-1 ${chartType === t ? "bg-indigo-600 text-white" : "bg-white text-gray-600"}`}>{t}</button>
              ))}
            </div>
          </div>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              {chartType === "line" ? (
                <LineChart data={chart.data}>
                  <CartesianGrid strokeDasharray="3 3" /><XAxis dataKey={chart.xKey} fontSize={12} tickFormatter={chart.timeX ? shortDate : undefined} /><YAxis allowDecimals fontSize={12} />
                  <Tooltip />{chart.keys.length > 1 && <Legend />}
                  {chart.keys.map((k, i) => <Line key={k} type="monotone" dataKey={k} stroke={COLORS[i % COLORS.length]} dot={false} strokeWidth={2} />)}
                </LineChart>
              ) : (
                <BarChart data={chart.data}>
                  <CartesianGrid strokeDasharray="3 3" /><XAxis dataKey={chart.xKey} fontSize={12} tickFormatter={chart.timeX ? shortDate : undefined} /><YAxis allowDecimals fontSize={12} />
                  <Tooltip />{chart.keys.length > 1 && <Legend />}
                  {chart.keys.map((k, i) => <Bar key={k} dataKey={k} stackId="a" fill={COLORS[i % COLORS.length]} />)}
                </BarChart>
              )}
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {rows.length > 0 && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {drillable && <div className="px-4 py-1.5 text-xs text-gray-400 border-b">Click a row to see the rows behind it ↓</div>}
          <RowsTable columns={columns} rows={rows} onRowClick={drillable ? runDrill : undefined} />
        </div>
      )}

      {drill && (
        <div className="bg-white rounded-lg shadow border-l-4 border-indigo-400">
          <div className="px-4 py-2 border-b flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">Rows · <span className="font-mono text-gray-500">{drill.label}</span></span>
            <button onClick={() => setDrill(null)} className="text-gray-300 hover:text-red-500">✕</button>
          </div>
          {drill.loading ? <p className="text-gray-400 p-4">Loading…</p>
            : drill.error ? <p className="text-red-500 p-4 text-sm font-mono">{drill.error}</p>
            : drill.result?.rows?.length ? (
              <>
                <div className="px-4 py-2 flex flex-wrap items-center gap-1 border-b bg-gray-50">
                  <span className="text-xs text-gray-400 mr-1">columns:</span>
                  {shownCols.map((n) => (
                    <button key={n} onClick={() => setCols(shownCols.filter((x) => x !== n))}
                      className="text-xs bg-white border border-gray-200 rounded px-1.5 py-0.5 hover:text-red-500" title="remove column">
                      {n} ✕
                    </button>
                  ))}
                  <AddColumn properties={properties} shown={shownCols} onAdd={(n) => setCols([...shownCols, n])} />
                </div>
                <div className="px-4 py-1 text-xs text-gray-400 font-mono truncate" title={drill.result.detail_query}>{drill.result.detail_query}</div>
                <RowsTable columns={drill.result.columns || []} rows={drill.result.rows} />
              </>
            ) : <p className="text-gray-500 p-4 text-sm">No matching rows.</p>}
        </div>
      )}
    </div>
  );
}

// "Each app" comparison: the same MetricsQL run against every app. Scalar queries
// collapse into one comparison table + bar chart (the fleet view); richer kinds
// stack a per-app ResultView. Each entry is { app, result } or { app, error }.
function EachAppView({ entries, chartType, setChartType }) {
  const ok = entries.filter((e) => e.result && e.result.kind);
  const allScalar = ok.length > 0 && ok.every((e) => e.result.kind === "scalar");

  if (allScalar) {
    const measures = [...new Set(ok.flatMap((e) => e.result.columns.filter((c) => c.role === "measure").map((c) => c.name)))];
    const cols = measures.length ? measures : Object.keys(ok[0].result.rows[0] || {});
    const rows = entries
      .map((e) => ({
        id: e.app.id,
        app: e.app.name || e.app.slug,
        error: e.error,
        vals: e.result ? Object.fromEntries(cols.map((m) => [m, e.result.rows[0]?.[m]])) : {},
      }))
      .sort((a, b) => (Number(b.vals[cols[0]]) || 0) - (Number(a.vals[cols[0]]) || 0));
    const chartData = rows.filter((r) => !r.error).map((r) => ({ app: r.app, ...r.vals }));

    return (
      <div className="space-y-4">
        <div className="text-xs text-gray-500">Each app · {entries.length} app{entries.length === 1 ? "" : "s"}</div>
        <div className="bg-white rounded-lg shadow overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
              <tr><th className="text-left px-4 py-2">App</th>{cols.map((m) => <th key={m} className="text-right px-4 py-2">{m}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((r) => (
                <tr key={r.id}>
                  <td className="px-4 py-2 font-medium text-gray-800">{r.app}</td>
                  {r.error
                    ? <td colSpan={cols.length} className="px-4 py-2 text-right text-red-400 font-mono text-xs">{r.error}</td>
                    : cols.map((m) => <td key={m} className="px-4 py-2 text-right tabular-nums">{num(r.vals[m])}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {chartData.length > 0 && (
          <div className="bg-white rounded-lg shadow p-6">
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="app" fontSize={12} /><YAxis allowDecimals fontSize={12} />
                  <Tooltip />{cols.length > 1 && <Legend />}
                  {cols.map((m, i) => <Bar key={m} dataKey={m} fill={COLORS[i % COLORS.length]} />)}
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Non-scalar (facets/timeseries/rows): stack each app's full result.
  return (
    <div className="space-y-6">
      <div className="text-xs text-gray-500">Each app · {entries.length} app{entries.length === 1 ? "" : "s"}</div>
      {entries.map((e) => (
        <div key={e.app.id} className="space-y-2">
          <h3 className="text-sm font-semibold text-gray-700">{e.app.name || e.app.slug}</h3>
          {e.error
            ? <div className="bg-red-50 border border-red-200 text-red-600 px-3 py-2 rounded text-xs font-mono">{e.error}</div>
            : <ResultView result={e.result} appId={String(e.app.id)} properties={[]} chartType={chartType} setChartType={setChartType} />}
        </div>
      ))}
    </div>
  );
}

export default function Explore() {
  const { currentContainer } = useAuth();
  const qc = useQueryClient();
  const [appId, setAppId] = useState("");
  const [text, setText] = useState(EXAMPLES[0]);
  const [ran, setRan] = useState("");
  const [chartType, setChartType] = useState("line");
  const [scope, setScope] = useState("this"); // "this" app | "each" app (fleet comparison)
  const [askText, setAskText] = useState("");
  const [asking, setAsking] = useState(false);
  const [askError, setAskError] = useState("");
  const editorRef = useRef(null);

  const { data: appsData } = useQuery({ queryKey: ["apps", currentContainer?.id], queryFn: api.getApps, enabled: !!currentContainer });
  const apps = Array.isArray(appsData) ? appsData : (appsData?.data || appsData?.apps || []);
  useEffect(() => { if (!appId && apps.length > 0) setAppId(String(apps[0].id)); }, [apps, appId]);

  const { data: savedReports } = useQuery({
    queryKey: ["saved-reports", currentContainer?.id],
    queryFn: () => api.getSavedReports(),
    enabled: !!currentContainer,
  });
  const saved = Array.isArray(savedReports) ? savedReports : [];

  // Scope property suggestions to the event named after FROM (if any), so we only
  // offer columns that event actually has (e.g. duration_ms on api_request, not
  // page_view). Event names stay unscoped for the FROM picker.
  const fromEvent = useMemo(() => {
    const m = text.match(/\bfrom\s+([A-Za-z_][\w.]*)/i);
    const src = m && m[1];
    if (!src || /^events$/i.test(src) || /^(sample|value)\./i.test(src)) return null;
    return src;
  }, [text]);

  const { data: props } = useQuery({
    queryKey: ["insight-props", currentContainer?.id, appId, fromEvent],
    queryFn: () => api.getInsightProperties(appId, fromEvent || undefined),
    enabled: !!currentContainer && !!appId,
  });
  const events = props?.events || [];
  const keys = props?.keys || [];
  const properties = props?.properties || keys.map((k) => ({ name: k, kind: "string" }));
  const samples = props?.sources?.samples || [];
  const values = props?.sources?.values || [];

  const { data: result, isFetching, error } = useQuery({
    queryKey: ["mql", appId, ran],
    queryFn: () => api.runQuery(appId, ran),
    enabled: scope === "this" && !!appId && !!ran,
    keepPreviousData: true,
    retry: false,
  });

  // "Each app": run the same query against every app in parallel; per-app failures
  // are captured (not thrown) so one app can't blank the whole comparison.
  const appIds = apps.map((a) => a.id).join(",");
  const { data: eachData, isFetching: eachFetching } = useQuery({
    queryKey: ["mql-each", appIds, ran],
    queryFn: () => Promise.all(apps.map((a) =>
      api.runQuery(a.id, ran).then((r) => ({ app: a, result: r })).catch((e) => ({ app: a, error: e.message })))),
    enabled: scope === "each" && !!ran && apps.length > 0,
    keepPreviousData: true,
    retry: false,
  });

  const run = () => setRan(text.trim());
  const insert = (token) => editorRef.current?.insertToken(token);

  // Plain-English → MetricsQL: compose against the selected app's schema, then
  // load the result into the editor and run it (in whatever scope is selected).
  const ask = async () => {
    const question = askText.trim();
    if (!question || !appId) return;
    setAsking(true); setAskError("");
    try {
      const res = await api.askQuery(appId, question);
      if (res?.query) { setText(res.query); setRan(res.query); }
      if (res && res.ok === false) setAskError(res.error || "Couldn't build a query — try rephrasing.");
    } catch (e) {
      setAskError(e.message || "Ask failed.");
    } finally {
      setAsking(false);
    }
  };
  const loadSaved = (report) => {
    const q = report?.queries?.[0];
    if (!q) return;
    setText(q);
    setRan(q);
  };
  const deleteSaved = async (report) => {
    if (!window.confirm(`Delete "${report.name}"?`)) return;
    await api.deleteSavedReport(report.id);
    qc.invalidateQueries({ queryKey: ["saved-reports", currentContainer?.id] });
  };

  const save = async () => {
    const q = ran || text.trim();
    if (!q) return;
    const name = window.prompt("Save this query as:", "");
    if (!name) return;
    try {
      await api.createSavedReport({ app_id: appId, name, queries: [q] });
      qc.invalidateQueries({ queryKey: ["saved-reports", currentContainer?.id] });
      window.alert("Saved — find it in the Saved queries list.");
    } catch (e) { window.alert(`Could not save: ${e.message}`); }
  };

  if (!currentContainer) return <div className="text-center py-12"><p className="text-gray-500">Loading account…</p></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Explore</h1>
          <p className="text-gray-500">Query your metrics with MetricsQL. Press ⌘/Ctrl+Enter to run.</p>
        </div>
        <button onClick={save} disabled={!appId || !(ran || text.trim())}
          className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-40">★ Save query</button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <div className="lg:col-span-3 space-y-4">
          <div className="bg-gradient-to-r from-indigo-50 to-white rounded-lg shadow p-3">
            <div className="flex items-center gap-2">
              <span className="text-indigo-500">✨</span>
              <input value={askText} onChange={(e) => setAskText(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") ask(); }}
                placeholder="Ask in plain English — e.g. “error rate by endpoint over the last 7 days”"
                className="flex-1 bg-transparent text-sm outline-none placeholder-gray-400" />
              <button onClick={ask} disabled={asking || !askText.trim() || !appId}
                className="px-3 py-1.5 bg-indigo-600 text-white rounded text-sm font-medium hover:bg-indigo-700 disabled:opacity-40">
                {asking ? "Thinking…" : "Ask AI"}
              </button>
            </div>
            {askError && <div className="text-xs text-red-500 mt-1 font-mono">{askError}</div>}
          </div>
          <div className="bg-white rounded-lg shadow p-4 space-y-3">
            <div className="flex items-center gap-3">
              <div className="flex rounded-lg border border-gray-200 overflow-hidden text-xs" title="Run against the selected app, or against every app (comparison)">
                {[["this", "This app"], ["each", "Each app"]].map(([v, label]) => (
                  <button key={v} onClick={() => setScope(v)}
                    className={`px-2.5 py-1.5 ${scope === v ? "bg-gray-900 text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}>{label}</button>
                ))}
              </div>
              <select value={appId} onChange={(e) => setAppId(e.target.value)} disabled={scope === "each"}
                className={`${ctl} min-w-[10rem] disabled:opacity-40`}>
                {apps.length === 0 && <option value="">No apps</option>}
                {apps.map((a) => <option key={a.id} value={a.id}>{a.name || a.slug}</option>)}
              </select>
              <button onClick={run} disabled={!text.trim() || (scope === "this" && !appId) || (scope === "each" && apps.length === 0)}
                className="ml-auto px-4 py-1.5 bg-gray-900 text-white rounded text-sm font-medium hover:bg-black disabled:opacity-40">
                Run ▸
              </button>
            </div>
            <QueryEditor ref={editorRef} value={text} onChange={setText} onRun={run} events={events} properties={properties} samples={samples} values={values} appId={appId} fromEvent={fromEvent} />
            <div className="flex flex-wrap gap-2">
              {EXAMPLES.map((ex, i) => (
                <button key={i} onClick={() => setText(ex)}
                  className="text-xs text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded px-2 py-1 font-mono truncate max-w-full" title={ex}>
                  {ex.length > 60 ? `${ex.slice(0, 60)}…` : ex}
                </button>
              ))}
            </div>
          </div>

          {scope === "this" ? (
            <>
              {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded text-sm font-mono">{error.message}</div>}
              {isFetching && !result && <p className="text-gray-400 text-center py-10">Running…</p>}
              {result && !error && <ResultView result={result} appId={appId} properties={properties} chartType={chartType} setChartType={setChartType} />}
            </>
          ) : (
            <>
              {eachFetching && !eachData && <p className="text-gray-400 text-center py-10">Running against {apps.length} apps…</p>}
              {eachData && <EachAppView entries={eachData} chartType={chartType} setChartType={setChartType} />}
            </>
          )}
        </div>

        {/* Schema reference sidebar */}
        <div className="space-y-4">
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="text-xs font-semibold text-gray-500 uppercase mb-2">Saved queries ({saved.length})</h3>
            {saved.length === 0 ? (
              <p className="text-xs text-gray-400">None yet — write one and hit ★ Save query.</p>
            ) : (
              <div className="space-y-0.5 max-h-72 overflow-y-auto">
                {saved.map((r) => (
                  <div key={r.id} className="group flex items-center gap-1">
                    <button onClick={() => loadSaved(r)} title={r.queries?.[0] || ""}
                      className="flex-1 text-left text-xs text-gray-700 hover:text-indigo-600 hover:bg-indigo-50 rounded px-1.5 py-1 truncate">
                      {r.name}
                    </button>
                    <button onClick={() => deleteSaved(r)} title="Delete"
                      className="text-gray-300 hover:text-red-500 text-xs px-1 opacity-0 group-hover:opacity-100">✕</button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="text-xs font-semibold text-gray-500 uppercase mb-2">Events</h3>
            <div className="flex flex-wrap gap-1 max-h-40 overflow-y-auto">
              {events.length === 0 && <span className="text-xs text-gray-400">—</span>}
              {events.map((e) => (
                <button key={e} onClick={() => insert(e)} className="text-xs bg-gray-100 hover:bg-gray-200 rounded px-1.5 py-0.5 font-mono">{e}</button>
              ))}
            </div>
          </div>
          {samples.length > 0 && (
            <div className="bg-white rounded-lg shadow p-4">
              <h3 className="text-xs font-semibold text-gray-500 uppercase mb-2">Samples ({samples.length})</h3>
              <div className="flex flex-wrap gap-1 max-h-40 overflow-y-auto">
                {samples.map((s) => (
                  <button key={s} onClick={() => insert(`sample.${s}`)} className="text-xs bg-teal-50 hover:bg-teal-100 text-teal-700 rounded px-1.5 py-0.5 font-mono">{s}</button>
                ))}
              </div>
            </div>
          )}
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="text-xs font-semibold text-gray-500 uppercase mb-2">Properties</h3>
            <div className="flex flex-wrap gap-1 max-h-60 overflow-y-auto">
              {keys.length === 0 && <span className="text-xs text-gray-400">—</span>}
              {keys.map((k) => (
                <button key={k} onClick={() => insert(k)} className="text-xs bg-gray-100 hover:bg-gray-200 rounded px-1.5 py-0.5 font-mono">{k}</button>
              ))}
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4 text-xs text-gray-500 space-y-1">
            <div className="font-semibold text-gray-600 uppercase mb-1">Cheatsheet</div>
            <div><span className="font-mono text-gray-700">SELECT</span> count(*), avg(f), uniqueCount(f)</div>
            <div><span className="font-mono text-gray-700">FROM</span> event / sample.m / value.m</div>
            <div><span className="font-mono text-gray-700">WHERE</span> = != &gt; &lt; IN LIKE (case-insensitive)</div>
            <div><span className="font-mono text-gray-700">FACET</span> · <span className="font-mono text-gray-700">TIMESERIES 1 hour</span></div>
            <div><span className="font-mono text-gray-700">SINCE</span> 24 hours ago · <span className="font-mono text-gray-700">ORDER BY</span> · <span className="font-mono text-gray-700">LIMIT</span></div>
          </div>
        </div>
      </div>
    </div>
  );
}
